import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../providers/AuthProvider";
import { createTmaApiClient } from "../services/tmaApiClient";
import { extractPayloadMessage } from "../utils/apiPayload";
import { isUuid } from "../utils/uuid";
import {
  formatWallClockDateTime,
  formatWallClockRange,
} from "../utils/wallClockDate";

type RequestsStatus = "idle" | "loading" | "ready" | "empty" | "error";
type SlotsStatus = "idle" | "loading" | "ready" | "empty" | "error";
type BookingStatus = "idle" | "submitting" | "success" | "error";
type CalendarActionStatus = "idle" | "submitting" | "success" | "error";

interface MakeupRequestItem {
  id: string;
  status: string;
  studentName: string | null;
  lessonNo: number | null;
  flowNo: string | null;
  offeredSlot: string | null;
  displayTimeZone: string | null;
}

interface MakeupSlotItem {
  id: string;
  slotStart: string;
  slotEnd: string | null;
  topic: string | null;
  flowNo: string | null;
  priority: number | null;
  displayTimeZone: string | null;
}

interface SelectSlotResult {
  alreadySelected?: boolean;
}

interface CalendarPayload {
  deeplinkUrl?: string;
  downloadUrl?: string;
}

interface AvailableSlotsProps {
  preferredLessonNo?: number | null;
}

interface StudentMakeupsQueryParams {
  status: "open" | "booked";
  flowNo?: string;
  studentId?: string;
  studentName?: string;
}

const AUTO_OFFERS_REFRESH_MS = 45_000;
const DEFAULT_DISPLAY_TIME_ZONE = "Asia/Almaty";

const resolveDisplayTimeZone = (
  rawValue: string | null | undefined,
): string => {
  const normalized = String(rawValue || "").trim();
  if (!normalized) {
    return DEFAULT_DISPLAY_TIME_ZONE;
  }

  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone: normalized }).format(
      new Date(),
    );
    return normalized;
  } catch {
    return DEFAULT_DISPLAY_TIME_ZONE;
  }
};

const formatSlotRange = (
  slotStart: string,
  slotEnd: string | null,
  _displayTimeZone: string,
): string => formatWallClockRange(slotStart, slotEnd);

const formatSlotDateTime = (
  slotStart: string,
  _displayTimeZone: string,
): string => formatWallClockDateTime(slotStart);

const parseRequestsPayload = (payload: unknown): MakeupRequestItem[] | null => {
  if (!Array.isArray(payload)) {
    return null;
  }

  return payload
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const source = item as Record<string, unknown>;
      const id = String(source.id ?? "").trim();
      if (!id) {
        return null;
      }

      return {
        id,
        status: String(source.status ?? "unknown"),
        studentName:
          typeof source.student === "string" && source.student.trim()
            ? source.student
            : null,
        lessonNo: typeof source.lessonNo === "number" ? source.lessonNo : null,
        flowNo:
          typeof source.flowNo === "string" && source.flowNo.trim()
            ? source.flowNo
            : null,
        offeredSlot:
          typeof source.offeredSlot === "string" && source.offeredSlot.trim()
            ? source.offeredSlot
            : null,
        displayTimeZone:
          typeof source.displayTimeZone === "string" &&
          source.displayTimeZone.trim()
            ? source.displayTimeZone.trim()
            : null,
      } satisfies MakeupRequestItem;
    })
    .filter((item): item is MakeupRequestItem => Boolean(item));
};

const extractSlotsArray = (payload: unknown): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const source = payload as { slots?: unknown };
    if (Array.isArray(source.slots)) {
      return source.slots;
    }
  }

  return null;
};

const parseSlotsPayload = (payload: unknown): MakeupSlotItem[] | null => {
  const slotsArray = extractSlotsArray(payload);
  const payloadDisplayTimeZone =
    payload &&
    typeof payload === "object" &&
    typeof (payload as { displayTimeZone?: unknown }).displayTimeZone ===
      "string"
      ? String((payload as { displayTimeZone: string }).displayTimeZone).trim()
      : null;

  if (!slotsArray) {
    return null;
  }

  return slotsArray
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const source = item as Record<string, unknown>;
      const id = String(source.id ?? "").trim();
      const slotStart = String(source.slotStart ?? "").trim();

      if (!id || !slotStart) {
        return null;
      }

      return {
        id,
        slotStart,
        slotEnd: typeof source.slotEnd === "string" ? source.slotEnd : null,
        topic:
          typeof source.topic === "string" && source.topic.trim()
            ? source.topic
            : null,
        flowNo:
          typeof source.flowNo === "string" && source.flowNo.trim()
            ? source.flowNo
            : null,
        priority: typeof source.priority === "number" ? source.priority : null,
        displayTimeZone:
          typeof source.displayTimeZone === "string" &&
          source.displayTimeZone.trim()
            ? source.displayTimeZone.trim()
            : payloadDisplayTimeZone,
      } satisfies MakeupSlotItem;
    })
    .filter((item): item is MakeupSlotItem => Boolean(item));
};

export const AvailableSlots = ({
  preferredLessonNo = null,
}: AvailableSlotsProps) => {
  const auth = useAuth();
  const apiClient = useMemo(
    () => createTmaApiClient(auth.session),
    [auth.session],
  );

  const [requestsStatus, setRequestsStatus] = useState<RequestsStatus>("idle");
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [requests, setRequests] = useState<MakeupRequestItem[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );

  const [bookedRequestsStatus, setBookedRequestsStatus] =
    useState<RequestsStatus>("idle");
  const [bookedRequestsError, setBookedRequestsError] = useState<string | null>(
    null,
  );
  const [bookedRequests, setBookedRequests] = useState<MakeupRequestItem[]>([]);
  const [selectedBookedRequest, setSelectedBookedRequest] =
    useState<MakeupRequestItem | null>(null);

  const [slotsStatus, setSlotsStatus] = useState<SlotsStatus>("idle");
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [slots, setSlots] = useState<MakeupSlotItem[]>([]);

  const [bookingStatus, setBookingStatus] = useState<BookingStatus>("idle");
  const [bookingMessage, setBookingMessage] = useState<string | null>(null);
  const [bookingSlotId, setBookingSlotId] = useState<string | null>(null);

  const [cancelBookingStatus, setCancelBookingStatus] =
    useState<BookingStatus>("idle");
  const [cancelBookingMessage, setCancelBookingMessage] = useState<
    string | null
  >(null);
  const [cancelBookingRequestId, setCancelBookingRequestId] = useState<
    string | null
  >(null);
  const [calendarActionStatusByRequestId, setCalendarActionStatusByRequestId] =
    useState<Record<string, CalendarActionStatus>>({});
  const [
    calendarActionMessageByRequestId,
    setCalendarActionMessageByRequestId,
  ] = useState<Record<string, string | null>>({});

  const queryStudentMakeups = useCallback(
    async ({
      status,
      flowNo,
      studentId,
      studentName,
    }: StudentMakeupsQueryParams): Promise<MakeupRequestItem[] | null> => {
      const makeRequest = async (flowFilter: string | undefined) =>
        apiClient.request({
          method: "GET",
          path: "/makeups",
          query: {
            status,
            flowNo: flowFilter,
            studentId,
            studentName,
          },
        });

      const primaryResult = await makeRequest(flowNo);

      if (!primaryResult.ok) {
        return null;
      }

      const primaryParsed = parseRequestsPayload(primaryResult.responsePayload);
      if (!primaryParsed) {
        return null;
      }

      if (primaryParsed.length > 0 || !flowNo || (!studentId && !studentName)) {
        return primaryParsed;
      }

      const fallbackResult = await makeRequest(undefined);
      if (!fallbackResult.ok) {
        return primaryParsed;
      }

      const fallbackParsed = parseRequestsPayload(
        fallbackResult.responsePayload,
      );
      if (!fallbackParsed) {
        return primaryParsed;
      }

      if (fallbackParsed.length > primaryParsed.length) {
        console.warn(
          "[AvailableSlots] makeup query fallback without flowNo returned more items",
          {
            status,
            flowNo,
            studentId,
            studentName,
            primaryCount: primaryParsed.length,
            fallbackCount: fallbackParsed.length,
          },
        );
        return fallbackParsed;
      }

      return primaryParsed;
    },
    [apiClient],
  );

  const activeRequest = useMemo(
    () => requests.find((item) => item.id === selectedRequestId) ?? null,
    [requests, selectedRequestId],
  );

  const effectiveBookedRequests = useMemo(() => {
    const byId = new Map<string, MakeupRequestItem>();

    bookedRequests.forEach((item) => byId.set(item.id, item));

    requests
      .filter((item) => Boolean(item.offeredSlot))
      .forEach((item) => {
        if (!byId.has(item.id)) {
          byId.set(item.id, {
            ...item,
            status: item.status === "open" ? "booked" : item.status,
          });
        }
      });

    return Array.from(byId.values());
  }, [bookedRequests, requests]);

  const loadRequests = useCallback(async () => {
    if (auth.status !== "authenticated" || !auth.session) {
      setRequestsStatus("error");
      setRequestsError(
        "Запись на отработку недоступна: отсутствует активная сессия студента.",
      );
      return;
    }

    if (auth.session.user.role !== "student") {
      setRequestsStatus("error");
      setRequestsError(
        "Запись на отработку доступна только для роли студента.",
      );
      return;
    }

    setRequestsStatus("loading");
    setRequestsError(null);

    const flowNo = auth.session.user.flowNo?.trim();
    const rawStudentId = auth.session.user.id?.trim();
    const resolvedStudentId = isUuid(rawStudentId) ? rawStudentId : undefined;
    const resolvedStudentName =
      !resolvedStudentId && auth.session.user.name?.trim()
        ? auth.session.user.name.trim()
        : undefined;

    const parsed = await queryStudentMakeups({
      status: "open",
      flowNo: flowNo || undefined,
      studentId: resolvedStudentId,
      studentName: resolvedStudentName,
    });

    if (!parsed) {
      setRequestsStatus("error");
      setRequestsError(
        "Не удалось загрузить заявки на отработку (ошибка запроса или некорректный payload).",
      );
      return;
    }

    console.info("[AvailableSlots] open requests loaded", {
      flowNo,
      studentId: resolvedStudentId ?? null,
      studentName: resolvedStudentName ?? null,
      count: parsed.length,
      lessonNos: parsed.map((x) => x.lessonNo),
      statuses: parsed.map((x) => x.status),
      ids: parsed.map((x) => x.id),
    });

    setRequests(parsed);

    if (!parsed.length) {
      setRequestsStatus("empty");
      setSelectedRequestId(null);
      setSlotsStatus("idle");
      setSlots([]);
      return;
    }

    setRequestsStatus("ready");
    const preferredId =
      preferredLessonNo !== null
        ? (parsed.find((item) => item.lessonNo === preferredLessonNo)?.id ??
          null)
        : null;

    setSelectedRequestId((current) => {
      if (preferredId) {
        return preferredId;
      }

      if (current && parsed.some((item) => item.id === current)) {
        return current;
      }

      return parsed[0].id;
    });
  }, [apiClient, auth.session, auth.status, preferredLessonNo]);

  const loadBookedRequests = useCallback(async () => {
    if (auth.status !== "authenticated" || !auth.session) {
      setBookedRequestsStatus("error");
      setBookedRequestsError(
        "Записи на отработку недоступны: отсутствует активная student session.",
      );
      return;
    }

    if (auth.session.user.role !== "student") {
      setBookedRequestsStatus("error");
      setBookedRequestsError(
        "Записи на отработку доступны только для роли студента.",
      );
      return;
    }

    setBookedRequestsStatus("loading");
    setBookedRequestsError(null);

    const flowNo = auth.session.user.flowNo?.trim();
    const rawStudentId = auth.session.user.id?.trim();
    const resolvedStudentId = isUuid(rawStudentId) ? rawStudentId : undefined;
    const resolvedStudentName =
      !resolvedStudentId && auth.session.user.name?.trim()
        ? auth.session.user.name.trim()
        : undefined;

    const parsed = await queryStudentMakeups({
      status: "booked",
      flowNo: flowNo || undefined,
      studentId: resolvedStudentId,
      studentName: resolvedStudentName,
    });

    if (!parsed) {
      setBookedRequestsStatus("error");
      setBookedRequestsError(
        "Не удалось загрузить записи на отработку (ошибка запроса или некорректный payload).",
      );
      return;
    }

    console.info("[AvailableSlots] booked requests loaded", {
      flowNo,
      studentId: resolvedStudentId ?? null,
      studentName: resolvedStudentName ?? null,
      count: parsed.length,
      lessonNos: parsed.map((x) => x.lessonNo),
      statuses: parsed.map((x) => x.status),
      ids: parsed.map((x) => x.id),
    });

    setBookedRequests(parsed);
    setBookedRequestsStatus(parsed.length ? "ready" : "empty");
  }, [auth.session, auth.status, queryStudentMakeups]);

  const generateAndListSlotsLegacy = useCallback(
    async (requestId: string): Promise<MakeupSlotItem[] | null> => {
      const generateResult = await apiClient.request({
        method: "POST",
        path: `/makeups/${requestId}/slots/generate`,
        body: {
          limit: 3,
        },
      });

      if (!generateResult.ok) {
        setSlotsError(
          `Не удалось подготовить слоты (${generateResult.status ?? "network"}): ${extractPayloadMessage(
            generateResult.responsePayload,
            generateResult.error ?? "Неизвестная ошибка",
          )}`,
        );
        return null;
      }

      const listResult = await apiClient.request({
        method: "GET",
        path: `/makeups/${requestId}/slots`,
      });

      if (!listResult.ok) {
        setSlotsError(
          `Не удалось загрузить слоты (${listResult.status ?? "network"}): ${extractPayloadMessage(
            listResult.responsePayload,
            listResult.error ?? "Неизвестная ошибка",
          )}`,
        );
        return null;
      }

      const parsed = parseSlotsPayload(listResult.responsePayload);
      if (!parsed) {
        setSlotsError(
          "GET /makeups/:id/slots вернул некорректный payload: ожидается массив слотов.",
        );
        return null;
      }

      return parsed.slice(0, 3);
    },
    [apiClient],
  );

  const loadTopSlotsForRequest = useCallback(
    async (
      requestId: string,
      options?: {
        forceRegenerate?: boolean;
        reason?: string;
      },
    ) => {
      const forceRegenerate = options?.forceRegenerate === true;
      setSlotsStatus("loading");
      setSlotsError(null);

      console.info("[AvailableSlots] refresh slots", {
        requestId,
        reason: options?.reason ?? "manual",
        forceRegenerate,
      });

      if (forceRegenerate) {
        const legacySlots = await generateAndListSlotsLegacy(requestId);
        if (!legacySlots) {
          setSlotsStatus("error");
          setSlots([]);
          return;
        }

        setSlots(legacySlots);
        setSlotsStatus(legacySlots.length ? "ready" : "empty");
        return;
      }

      const topResult = await apiClient.request({
        method: "GET",
        path: `/makeups/${requestId}/slots/top`,
        query: {
          limit: "3",
        },
      });

      if (topResult.ok) {
        const parsed = parseSlotsPayload(topResult.responsePayload);
        if (parsed) {
          if (!parsed.length) {
            const regeneratedSlots =
              await generateAndListSlotsLegacy(requestId);
            if (!regeneratedSlots) {
              setSlotsStatus("error");
              setSlots([]);
              return;
            }

            setSlots(regeneratedSlots);
            setSlotsStatus(regeneratedSlots.length ? "ready" : "empty");
            return;
          }

          setSlots(parsed.slice(0, 3));
          setSlotsStatus(parsed.length ? "ready" : "empty");
          return;
        }
      }

      const fallbackSlots = await generateAndListSlotsLegacy(requestId);
      if (!fallbackSlots) {
        setSlotsStatus("error");
        setSlots([]);
        return;
      }

      setSlots(fallbackSlots);
      setSlotsStatus(fallbackSlots.length ? "ready" : "empty");
    },
    [apiClient, generateAndListSlotsLegacy],
  );

  const bookSlotOneClick = useCallback(
    async (slot: MakeupSlotItem) => {
      if (!selectedRequestId) {
        return;
      }

      setBookingStatus("submitting");
      setBookingSlotId(slot.id);
      setBookingMessage(null);

      const result = await apiClient.request({
        method: "POST",
        path: `/makeups/${selectedRequestId}/select-slot`,
        body: {
          slotId: slot.id,
        },
      });

      if (!result.ok) {
        setBookingStatus("error");
        setBookingMessage(
          `Не удалось записаться (${result.status ?? "network"}): ${extractPayloadMessage(
            result.responsePayload,
            result.error ?? "Неизвестная ошибка",
          )}`,
        );
        setBookingSlotId(null);
        return;
      }

      const payload =
        (result.responsePayload as SelectSlotResult | undefined) ?? {};
      const slotLabel = formatSlotRange(
        slot.slotStart,
        slot.slotEnd,
        resolveDisplayTimeZone(slot.displayTimeZone),
      );

      console.info("[AvailableSlots] select-slot success", {
        selectedRequestId,
        selectedSlotId: slot.id,
        alreadySelected: Boolean(payload.alreadySelected),
      });

      setBookingStatus("success");
      setBookingMessage(
        payload.alreadySelected
          ? `Вы уже были записаны на этот слот: ${slotLabel}.`
          : `Готово! Записали на отработку: ${slotLabel}.`,
      );
      setBookingSlotId(null);

      await Promise.all([loadRequests(), loadBookedRequests()]);

      console.info("[AvailableSlots] post-select reload finished", {
        selectedRequestId,
      });
    },
    [apiClient, loadBookedRequests, loadRequests, selectedRequestId],
  );

  const cancelBookedRequest = useCallback(
    async (request: MakeupRequestItem) => {
      const confirmed = window.confirm(
        "Отменить запись на отработку? Заявка снова станет открытой.",
      );
      if (!confirmed) {
        return;
      }

      setCancelBookingStatus("submitting");
      setCancelBookingMessage(null);
      setCancelBookingRequestId(request.id);

      const result = await apiClient.request({
        method: "PATCH",
        path: `/makeups/${request.id}/status`,
        body: {
          status: "open",
          offeredSlot: "",
        },
      });

      if (!result.ok) {
        setCancelBookingStatus("error");
        setCancelBookingMessage(
          `Не удалось отменить запись (${result.status ?? "network"}): ${extractPayloadMessage(
            result.responsePayload,
            result.error ?? "Неизвестная ошибка",
          )}`,
        );
        setCancelBookingRequestId(null);
        return;
      }

      setCancelBookingStatus("success");
      setCancelBookingMessage(
        "Запись отменена. Заявка снова открыта для выбора нового слота.",
      );
      setCancelBookingRequestId(null);

      if (selectedBookedRequest?.id === request.id) {
        setSelectedBookedRequest(null);
      }

      await Promise.all([loadRequests(), loadBookedRequests()]);
      setSelectedRequestId(request.id);
    },
    [apiClient, loadBookedRequests, loadRequests, selectedBookedRequest?.id],
  );

  const addBookedRequestToCalendar = useCallback(
    async (request: MakeupRequestItem) => {
      setCalendarActionStatusByRequestId((prev) => ({
        ...prev,
        [request.id]: "submitting",
      }));
      setCalendarActionMessageByRequestId((prev) => ({
        ...prev,
        [request.id]: null,
      }));

      const result = await apiClient.request({
        method: "GET",
        path: `/makeups/${request.id}/calendar`,
      });

      if (!result.ok) {
        setCalendarActionStatusByRequestId((prev) => ({
          ...prev,
          [request.id]: "error",
        }));
        setCalendarActionMessageByRequestId((prev) => ({
          ...prev,
          [request.id]: `Не удалось получить календарь (${result.status ?? "network"}): ${extractPayloadMessage(
            result.responsePayload,
            result.error ?? "Неизвестная ошибка",
          )}`,
        }));
        return;
      }

      const payload =
        (result.responsePayload as CalendarPayload | undefined) ?? {};
      const deeplinkUrl =
        typeof payload.deeplinkUrl === "string" && payload.deeplinkUrl.trim()
          ? payload.deeplinkUrl.trim()
          : null;
      const downloadUrl =
        typeof payload.downloadUrl === "string" && payload.downloadUrl.trim()
          ? payload.downloadUrl.trim()
          : null;

      let opened = false;

      if (deeplinkUrl) {
        const popup = window.open(deeplinkUrl, "_blank", "noopener,noreferrer");
        opened = Boolean(popup);
      }

      if (!opened && downloadUrl) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
        opened = true;
      }

      if (!opened) {
        setCalendarActionStatusByRequestId((prev) => ({
          ...prev,
          [request.id]: "error",
        }));
        setCalendarActionMessageByRequestId((prev) => ({
          ...prev,
          [request.id]:
            "Не удалось открыть календарь автоматически. Проверьте, что в заявке есть calendar link.",
        }));
        return;
      }

      setCalendarActionStatusByRequestId((prev) => ({
        ...prev,
        [request.id]: "success",
      }));
      setCalendarActionMessageByRequestId((prev) => ({
        ...prev,
        [request.id]: deeplinkUrl
          ? "Открыли Google Calendar. Если окно заблокировано, используйте повтор."
          : "Google deeplink недоступен, скачали ICS как fallback.",
      }));
    },
    [apiClient],
  );

  useEffect(() => {
    void loadRequests();
    void loadBookedRequests();
  }, [loadBookedRequests, loadRequests]);

  useEffect(() => {
    if (!selectedRequestId || requestsStatus !== "ready") {
      setSlotsStatus("idle");
      setSlots([]);
      return;
    }

    void loadTopSlotsForRequest(selectedRequestId, {
      reason: "selected-request-or-open-list-change",
    });
  }, [loadTopSlotsForRequest, requestsStatus, selectedRequestId]);

  useEffect(() => {
    if (!selectedRequestId || requestsStatus !== "ready") {
      return;
    }

    const timer = window.setInterval(() => {
      void loadTopSlotsForRequest(selectedRequestId, {
        reason: "auto-interval",
      });
    }, AUTO_OFFERS_REFRESH_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [loadTopSlotsForRequest, requestsStatus, selectedRequestId]);

  useEffect(() => {
    if (!selectedRequestId || requestsStatus !== "ready") {
      return;
    }

    const refreshOnVisibility = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      void loadTopSlotsForRequest(selectedRequestId, {
        reason: "window-focus-or-visible",
      });
    };

    window.addEventListener("focus", refreshOnVisibility);
    document.addEventListener("visibilitychange", refreshOnVisibility);

    return () => {
      window.removeEventListener("focus", refreshOnVisibility);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  }, [loadTopSlotsForRequest, requestsStatus, selectedRequestId]);

  return (
    <>
      <h2 className="content-title">Отработка</h2>
      <p className="hint">
        Выберите слот в следующем подходящем потоке и сразу запишитесь на
        пропущенный урок.
      </p>

      <section className="panel">
        <h3 className="compact-title">Как это работает</h3>
        <p className="hint flush-bottom">
          Открытые пропуски отображаются ниже. Для выбранного урока приложение
          предлагает ближайшие слоты в следующих потоках.
        </p>
      </section>

      {requestsStatus === "loading" ? (
        <LoadingSpinner label="Загружаем открытые заявки на отработку..." />
      ) : null}

      {requestsStatus === "error" ? (
        <div className="alert">
          <p className="alert-text">
            {requestsError ?? "Неизвестная ошибка заявок на отработку"}
          </p>
          <button
            type="button"
            className="action-button"
            onClick={() => void loadRequests()}
          >
            Повторить попытку
          </button>
        </div>
      ) : null}

      {requestsStatus === "empty" ? (
        <div className="empty-state" role="status" aria-live="polite">
          <div className="empty-state-icon" aria-hidden="true">
            ✅
          </div>
          <p className="empty-state-text">
            Сейчас нет открытых заявок на отработку.
          </p>
        </div>
      ) : null}

      {requestsStatus === "ready" && requests.length > 0 ? (
        <section
          className="makeup-booking-layout"
          aria-label="Student one-click makeup booking flow"
        >
          <div>
            <h3 className="booking-section-title">Пропущенные уроки</h3>
            <ul className="booking-request-list">
              {requests.map((request) => {
                const isActive = request.id === selectedRequestId;

                return (
                  <li key={request.id}>
                    <button
                      type="button"
                      className={`booking-request-button ${isActive ? "is-active" : ""}`}
                      aria-pressed={isActive}
                      onClick={() => {
                        setSelectedRequestId(request.id);
                        setBookingStatus("idle");
                        setBookingMessage(null);
                      }}
                    >
                      <span className="booking-request-title">
                        Пропуск: занятие {request.lessonNo ?? "—"}
                      </span>
                      <span className="booking-request-meta">
                        {request.flowNo
                          ? `Поток ${request.flowNo}`
                          : "Поток не указан"}
                        {" · "}
                        {request.studentName ?? "Студент"}
                      </span>
                      <span className="booking-request-status">
                        {request.status === "open"
                          ? "Ожидает записи"
                          : request.status}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            {activeRequest ? (
              <div className="booking-active-banner">
                <strong>
                  Предложения для отработки занятия{" "}
                  {activeRequest.lessonNo ?? "—"}.
                </strong>
                <span>
                  Список обновляется автоматически. Выберите слот и запишитесь
                  сразу, без дополнительного подтверждения.
                </span>
              </div>
            ) : null}

            {slotsStatus === "loading" ? (
              <LoadingSpinner label="Подбираем лучшие слоты..." />
            ) : null}

            {slotsStatus === "error" ? (
              <div className="alert">
                <p className="alert-text">
                  {slotsError ?? "Неизвестная ошибка слотов"}
                </p>
                <button
                  type="button"
                  className="action-button"
                  onClick={() =>
                    selectedRequestId &&
                    void loadTopSlotsForRequest(selectedRequestId)
                  }
                >
                  Повторить загрузку
                </button>
              </div>
            ) : null}

            {slotsStatus === "empty" ? (
              <div className="empty-state" role="status" aria-live="polite">
                <div className="empty-state-icon" aria-hidden="true">
                  ⏱️
                </div>
                <p className="empty-state-text">
                  Слоты пока не найдены. Мы пересобираем предложения
                  автоматически.
                </p>
              </div>
            ) : null}

            {slotsStatus === "ready" && slots.length > 0 ? (
              <ul className="booking-slot-list">
                {slots.map((slot) => (
                  <li key={slot.id} className="booking-slot-card">
                    <div className="booking-slot-header">
                      <span className="booking-slot-time">
                        {formatSlotRange(
                          slot.slotStart,
                          slot.slotEnd,
                          resolveDisplayTimeZone(slot.displayTimeZone),
                        )}
                      </span>
                      <span className="booking-slot-flow-badge">
                        {slot.flowNo ? `Поток ${slot.flowNo}` : "Поток —"}
                      </span>
                    </div>

                    <p className="booking-slot-meta">
                      {slot.topic ? slot.topic : "Тема будет уточнена"}
                    </p>

                    <button
                      type="button"
                      className="booking-quick-action"
                      disabled={bookingStatus === "submitting"}
                      onClick={() => void bookSlotOneClick(slot)}
                    >
                      {bookingStatus === "submitting" &&
                      bookingSlotId === slot.id
                        ? "Записываем..."
                        : "Записаться"}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {bookingStatus === "error" ? (
              <div className="alert">{bookingMessage ?? "Ошибка записи"}</div>
            ) : null}

            {bookingStatus === "success" ? (
              <div className="booking-success" role="status" aria-live="polite">
                {bookingMessage}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section
        className="booking-history-section"
        aria-label="Записи на отработку"
      >
        <h3 className="booking-section-title">Записи на отработку</h3>

        {bookedRequestsStatus === "loading" ? (
          <LoadingSpinner label="Загружаем записи на отработку..." />
        ) : null}

        {bookedRequestsStatus === "error" ? (
          <div className="alert">
            <p className="alert-text">
              {bookedRequestsError ?? "Неизвестная ошибка записей на отработку"}
            </p>
            <button
              type="button"
              className="action-button"
              onClick={() => void loadBookedRequests()}
            >
              Повторить попытку
            </button>
          </div>
        ) : null}

        {(bookedRequestsStatus === "empty" ||
          bookedRequestsStatus === "ready") &&
        effectiveBookedRequests.length === 0 ? (
          <p className="hint">Пока нет записей на отработку.</p>
        ) : null}

        {effectiveBookedRequests.length > 0 ? (
          <ul className="booking-request-list">
            {effectiveBookedRequests.map((request) => {
              const calendarStatus =
                calendarActionStatusByRequestId[request.id] ?? "idle";
              const calendarMessage =
                calendarActionMessageByRequestId[request.id];

              return (
                <li key={request.id} className="booking-slot-card">
                  <button
                    type="button"
                    className="booking-request-button"
                    onClick={() => setSelectedBookedRequest(request)}
                  >
                    <span className="booking-request-title">
                      Запись: занятие {request.lessonNo ?? "—"}
                    </span>
                    <span className="booking-request-meta">
                      {request.flowNo
                        ? `Поток ${request.flowNo}`
                        : "Поток не указан"}
                      {" · "}
                      {request.studentName ?? "Студент"}
                    </span>
                    <span className="booking-request-meta">
                      {request.offeredSlot
                        ? `Слот: ${formatSlotRange(
                            request.offeredSlot,
                            null,
                            resolveDisplayTimeZone(request.displayTimeZone),
                          )}`
                        : "Слот: уточняется"}
                    </span>
                    <span className="booking-request-status">
                      {request.status === "open"
                        ? "Записан на отработку"
                        : request.status}
                    </span>
                  </button>

                  <div className="action-row">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => void addBookedRequestToCalendar(request)}
                      disabled={calendarStatus === "submitting"}
                    >
                      {calendarStatus === "submitting"
                        ? "Открываем календарь..."
                        : "Добавить в календарь"}
                    </button>

                    <button
                      type="button"
                      className="action-button"
                      onClick={() => void cancelBookedRequest(request)}
                      disabled={cancelBookingStatus === "submitting"}
                    >
                      {cancelBookingStatus === "submitting" &&
                      cancelBookingRequestId === request.id
                        ? "Отменяем запись..."
                        : "Отменить запись"}
                    </button>
                  </div>

                  {calendarMessage ? (
                    <p
                      className={
                        calendarStatus === "error"
                          ? "alert status-note"
                          : "hint status-note"
                      }
                    >
                      {calendarMessage}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {cancelBookingStatus === "error" ? (
          <div className="alert">
            {cancelBookingMessage ?? "Ошибка отмены записи"}
          </div>
        ) : null}

        {cancelBookingStatus === "success" ? (
          <div className="booking-success" role="status" aria-live="polite">
            {cancelBookingMessage}
          </div>
        ) : null}
      </section>

      {selectedBookedRequest ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Информация о записи на отработку"
          className="modal-backdrop"
          onClick={() => setSelectedBookedRequest(null)}
        >
          <div
            className="panel modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="compact-title">Информация о записи на отработку</h3>
            <p className="hint flush-top">
              Поток: {selectedBookedRequest.flowNo ?? "—"} · Урок:{" "}
              {selectedBookedRequest.lessonNo ?? "—"}
            </p>
            <ul className="compact-list">
              <li>Номер заявки: {selectedBookedRequest.id}</li>
              <li>
                Статус:{" "}
                {selectedBookedRequest.status === "open"
                  ? "Записан на отработку"
                  : selectedBookedRequest.status}
              </li>
              <li>
                Слот:{" "}
                {selectedBookedRequest.offeredSlot
                  ? formatSlotDateTime(
                      selectedBookedRequest.offeredSlot,
                      resolveDisplayTimeZone(
                        selectedBookedRequest.displayTimeZone,
                      ),
                    )
                  : "Слот уточняется"}
              </li>
              <li>Студент: {selectedBookedRequest.studentName ?? "—"}</li>
            </ul>

            <div className="action-row">
              <button
                type="button"
                className="action-button"
                onClick={() => void cancelBookedRequest(selectedBookedRequest)}
                disabled={cancelBookingStatus === "submitting"}
              >
                {cancelBookingStatus === "submitting" &&
                cancelBookingRequestId === selectedBookedRequest.id
                  ? "Отменяем запись..."
                  : "Отменить запись"}
              </button>
              <button
                type="button"
                className="action-button"
                onClick={() => setSelectedBookedRequest(null)}
                disabled={cancelBookingStatus === "submitting"}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
